import React, { useEffect } from "react";
import CompanyHeading from "../../../../theming/CompanyHeading";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Grid } from "@material-ui/core";
import { AppState } from "../../../../../redux";
import Loader from "../../../../theming/loader/Loader";
import { getInvoicesByOrganizationId } from "../../../../../redux/organizationInvoices/action";
import { getCancellationInvoicesByOrganizationId } from "../../../../../redux/cancellationInvoices/actions";
import { ExpandMore } from "@material-ui/icons";
import { useAccordionState } from "../../../../../hooks/useAccordionState";
import InvoiceList from "./InvoicesList";

interface InvoiceManagementProps {
  organizationId: string;
}

/**
 * Component for managing organization invoices.
 * @param organizationId - The id of the organization.
 */
export const InvoiceManagement: React.FC<InvoiceManagementProps> = (props) => {
  const { organizationId } = props;
  const { t } = useTranslation(["organization"]);
  const dispatch = useDispatch();

  const organizationInvoiceList = useSelector(
    (state: AppState) => state.organizationInvoice
  );
  const organizationInvoices = organizationInvoiceList.organizationInvoiceData;

  const cancellationInvoiceList = useSelector(
    (state: AppState) => state.cancellationInvoices
  );
  const cancellationInvoices = cancellationInvoiceList.invoiceData;

  const [expanded, handleChange] = useAccordionState("expandedPanel");

  useEffect(() => {
    dispatch(getInvoicesByOrganizationId(organizationId));
    dispatch(getCancellationInvoicesByOrganizationId(organizationId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  if (organizationInvoiceList?.isLoading) return <Loader />;

  return (
    <Grid>
      <Grid item xs={1}>
        <CompanyHeading>
          {t("invoice.showInvoices", { ns: "organization" })}
        </CompanyHeading>
      </Grid>
      <Accordion
        expanded={expanded === "organizationInvoices"}
        onChange={handleChange("organizationInvoices")}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          {t("invoice.manageInvoices", {
            ns: "organization",
            number: 0,
          })}
        </AccordionSummary>
        <AccordionDetails>
          <InvoiceList organizationInvoiceData={organizationInvoices} />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "cancellationInvoices"}
        onChange={handleChange("cancellationInvoices")}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel2-content"
          id="panel2-header"
        >
          {t("invoice.cancellationInvoices", {
            ns: "organization",
            number: 0,
          })}
        </AccordionSummary>
        <AccordionDetails>
          <InvoiceList
            organizationInvoiceData={cancellationInvoices}
            isCancellationInvoice
          />
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default InvoiceManagement;
